<script setup lang="ts">
import { ref, VNode, render, h, Component, isVNode } from "vue";
import {
  createTask,
  generateUUID,
  ScheduleDependOnResult
} from "@zyzgroup/core-common";
import {
  Easing,
  EasingFunction,
  // CSSToJSEasing,
  // reverseCSSToJSEasing,
  SupportedEaseNameToCubicBezier
} from "@zyzgroup/core-web";
// import { elementsToVNodes } from "../vnode";
import Page, { SupportedPageEffect } from "./Page.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Page },
  inheritAttrs: true
});

export type SupportedRouteEffect =
  | "moveToLeft"
  | "moveToRight"
  | "moveToTop"
  | "moveToBottom"
  | "moveFromLeftPrevFade"
  | "moveFromRightPrevFade"
  | "moveFromTopPrevFade"
  | "moveFromBottomPrevFade"
  | "moveToLeftFade"
  | "moveToRightFade"
  | "moveToTopFade"
  | "moveToBottomFade"
  | "moveToLeftEasing"
  | "moveToRightEasing"
  | "moveToTopEasing"
  | "moveToBottomEasing"
  | "moveFromLeftPrevScaleToSmall"
  | "moveFromRightPrevScaleToSmall"
  | "moveFromTopPrevScaleToSmall"
  | "moveFromBottomPrevScaleToSmall" // ⭐
  | "scaleSmall"
  | "scaleLarge"
  | "scaleToLargePrevScaleToSmall"
  | "scaleFromSmallPrevMoveToLeft"
  | "scaleFromSmallPrevMoveToRight"
  | "scaleFromSmallPrevMoveToTop"
  | "scaleFromSmallPrevMoveToBottom"
  | "glueToLeft"
  | "glueToRight"
  | "glueToTop"
  | "glueToBottom"
  | "flipToLeft"
  | "flipToRight"
  | "flipToTop"
  | "flipToBottom"
  | "rotateFall"
  | "newspaper"
  | "moveFromLeftPrevPush"
  | "moveFromRightPrevPush"
  | "moveFromTopPrevPush"
  | "moveFromBottomPrevPush"
  | "pullFromLeftPrevPush"
  | "pullFromRightPrevPush"
  | "pullFromTopPrevPush"
  | "pullFromBottomPrevPush"
  | "moveFromLeftFadePrevFold"
  | "moveFromRightFadePrevFold"
  | "moveFromTopFadePrevFold"
  | "moveFromBottomFadePrevFold"
  | "moveToLeftFadePrevUnfold"
  | "moveToRightFadePrevUnfold"
  | "moveToTopFadePrevUnfold"
  | "moveToBottomFadePrevUnfold"
  | "rotateRoomLeftIn"
  | "rotateRoomRightIn"
  | "rotateRoomTopIn"
  | "rotateRoomBottomIn"
  | "rotateCubeLeftIn"
  | "rotateCubeRightIn"
  | "rotateCubeTopIn"
  | "rotateCubeBottomIn"
  | "rotateCarouselLeftIn"
  | "rotateCarouselRightIn"
  | "rotateCarouselTopIn"
  | "rotateCarouselBottomIn"
  | "rotateSidesIn"
  | "rotateSlideIn";

const props = withDefaults(
  defineProps<{
    effect?: SupportedRouteEffect;
    duration?: number;
    // js ease
    easeFunction?: EasingFunction;
    // differentEaseFunction?: EasingFunction;
    // css ease
    timingFunction?: SupportedEaseNameToCubicBezier;
    delay?: number;
  }>(),
  {
    effect: "moveToLeft",
    duration: 500,
    easeFunction: Easing.Cubic.InOut,
    // differentEaseFunction: Easing.Quintic.InOut,
    timingFunction: "ease",
    delay: 0
  }
);

const emits = defineEmits<{
  (
    event: "routeChangeStart",
    fromPageIndex: number,
    toPageIndex: number,
    totalPages: number
  ): void;
  (
    event: "routeChangeEnd",
    fromPageIndex: number,
    toPageIndex: number,
    totalPages: number
  ): void;
  (event: "renderComplete"): void;
}>();

type VNodeChildAtom =
  | VNode
  | string
  | number
  | boolean
  | null
  | undefined
  | void;
type VNodeArrayChildren = Array<VNodeArrayChildren | VNodeChildAtom>;
type RawChildren =
  | string
  | number
  | boolean
  | VNode
  | VNodeArrayChildren
  | (() => any);
type RawSlots = {
  [name: string]: unknown;
  $stable?: boolean;
};

export type PageArgument = {
  pageKey?: string;
  // pageAttributes?: InstanceType<typeof Page>;
  component: VNode | Component | HTMLElement;
  attributes?: Record<string, any>;
  child?: RawChildren | RawSlots;
};

let currentPageIndex = -1;
let nextPageIndex = -1;

let onRenderComplete: () => void;

const onRouteCompletes: (() => void)[] = [];

let pages: PageArgument[] = [];
let pageActions: {
  open: () => void;
  close: () => void;
}[] = [];
let pageElements: HTMLElement[] = [];

function isValidPageIndex(pageIndex: number): boolean {
  return pageIndex >= 0 && pageIndex < pages.length;
}

function isForward(): boolean {
  return nextPageIndex > currentPageIndex;
}

// page effect
function _getPageEffect(
  pageIndex: number,
  currentPageEffect: SupportedPageEffect,
  nextPageEffect: SupportedPageEffect
): SupportedPageEffect {
  switch (pageIndex) {
    case currentPageIndex:
      return isForward() ? currentPageEffect : nextPageEffect;
    case nextPageIndex:
      return isForward() ? nextPageEffect : currentPageEffect;
    default:
      return "static";
  }
}
function getPageEffect(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): SupportedPageEffect {
  switch (routeEffect) {
    case "moveToLeft":
      return _getPageEffect(pageIndex, "moveToLeft", "moveFromRight");
    case "moveToRight":
      return _getPageEffect(pageIndex, "moveToRight", "moveFromLeft");
    case "moveToTop":
      return _getPageEffect(pageIndex, "moveToTop", "moveFromBottom");
    case "moveToBottom":
      return _getPageEffect(pageIndex, "moveToBottom", "moveFromTop");

    case "moveFromLeftPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromLeft");
    case "moveFromRightPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromRight");
    case "moveFromTopPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromTop");
    case "moveFromBottomPrevFade":
      return _getPageEffect(pageIndex, "fade", "moveFromBottom");

    case "moveToLeftFade":
      return _getPageEffect(pageIndex, "moveToLeftFade", "moveFromRightFade");
    case "moveToRightFade":
      return _getPageEffect(pageIndex, "moveToRightFade", "moveFromLeftFade");
    case "moveToTopFade":
      return _getPageEffect(pageIndex, "moveToTopFade", "moveFromBottomFade");
    case "moveToBottomFade":
      return _getPageEffect(pageIndex, "moveToBottomFade", "moveFromTopFade");

    case "moveToLeftEasing":
      return _getPageEffect(pageIndex, "moveToLeft", "moveFromRight");
    case "moveToRightEasing":
      return _getPageEffect(pageIndex, "moveToRight", "moveFromLeft");
    case "moveToTopEasing":
      return _getPageEffect(pageIndex, "moveToTop", "moveFromBottom");
    case "moveToBottomEasing":
      return _getPageEffect(pageIndex, "moveToBottom", "moveFromTop");

    case "moveFromLeftPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromLeft");
    case "moveFromRightPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromRight");
    case "moveFromTopPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromTop");
    case "moveFromBottomPrevScaleToSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "moveFromBottom");

    case "scaleSmall":
      return _getPageEffect(pageIndex, "scaleToSmall", "scaleFromLarge");
    case "scaleLarge":
      return _getPageEffect(pageIndex, "scaleToLarge", "scaleFromSmall");
    case "scaleToLargePrevScaleToSmall":
      return _getPageEffect(
        pageIndex,
        "scaleToSmallCenter",
        "scaleFromSmallCenter"
      );

    case "scaleFromSmallPrevMoveToLeft":
      return _getPageEffect(pageIndex, "moveToLeft", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToRight":
      return _getPageEffect(pageIndex, "moveToRight", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToTop":
      return _getPageEffect(pageIndex, "moveToTop", "scaleFromSmall");
    case "scaleFromSmallPrevMoveToBottom":
      return _getPageEffect(pageIndex, "moveToBottom", "scaleFromSmall");

    case "glueToLeft":
      return _getPageEffect(pageIndex, "rotateRightSideFirst", "moveFromRight");
    case "glueToRight":
      return _getPageEffect(pageIndex, "rotateLeftSideFirst", "moveFromLeft");
    case "glueToTop":
      return _getPageEffect(
        pageIndex,
        "rotateBottomSideFirst",
        "moveFromBottom"
      );
    case "glueToBottom":
      return _getPageEffect(pageIndex, "rotateTopSideFirst", "moveFromTop");

    case "flipToLeft":
      return _getPageEffect(pageIndex, "flipOutLeft", "flipInRight");
    case "flipToRight":
      return _getPageEffect(pageIndex, "flipOutRight", "flipInLeft");
    case "flipToTop":
      return _getPageEffect(pageIndex, "flipOutTop", "flipInBottom");
    case "flipToBottom":
      return _getPageEffect(pageIndex, "flipOutBottom", "flipInTop");

    case "rotateFall":
      return _getPageEffect(pageIndex, "rotateFall", "scaleFromSmall");

    case "newspaper":
      return _getPageEffect(
        pageIndex,
        "rotateOutNewspaper",
        "rotateInNewspaper"
      );

    case "moveFromLeftPrevPush":
      return _getPageEffect(pageIndex, "rotatePushRight", "moveFromLeft");
    case "moveFromRightPrevPush":
      return _getPageEffect(pageIndex, "rotatePushLeft", "moveFromRight");
    case "moveFromTopPrevPush":
      return _getPageEffect(pageIndex, "rotatePushBottom", "moveFromTop");
    case "moveFromBottomPrevPush":
      return _getPageEffect(pageIndex, "rotatePushTop", "moveFromBottom");

    case "pullFromLeftPrevPush":
      return _getPageEffect(pageIndex, "rotatePushRight", "rotatePullLeft");
    case "pullFromRightPrevPush":
      return _getPageEffect(pageIndex, "rotatePushLeft", "rotatePullRight");
    case "pullFromTopPrevPush":
      return _getPageEffect(pageIndex, "rotatePushBottom", "rotatePullTop");
    case "pullFromBottomPrevPush":
      return _getPageEffect(pageIndex, "rotatePushTop", "rotatePullBottom");

    case "moveFromLeftFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldRight", "moveFromLeftFade");
    case "moveFromRightFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldLeft", "moveFromRightFade");
    case "moveFromTopFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldBottom", "moveFromTopFade");
    case "moveFromBottomFadePrevFold":
      return _getPageEffect(pageIndex, "rotateFoldTop", "moveFromBottomFade");

    case "moveToLeftFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToLeftFade", "rotateUnfoldRight");
    case "moveToRightFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToRightFade", "rotateUnfoldLeft");
    case "moveToTopFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToTopFade", "rotateUnfoldBottom");
    case "moveToBottomFadePrevUnfold":
      return _getPageEffect(pageIndex, "moveToBottomFade", "rotateUnfoldTop");

    case "rotateRoomLeftIn":
      return _getPageEffect(pageIndex, "rotateRoomLeftOut", "rotateRoomLeftIn");
    case "rotateRoomRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateRoomRightOut",
        "rotateRoomRightIn"
      );
    case "rotateRoomTopIn":
      return _getPageEffect(pageIndex, "rotateRoomTopOut", "rotateRoomTopIn");
    case "rotateRoomBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateRoomBottomOut",
        "rotateRoomBottomIn"
      );

    case "rotateCubeLeftIn":
      return _getPageEffect(pageIndex, "rotateCubeLeftOut", "rotateCubeLeftIn");
    case "rotateCubeRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateCubeRightOut",
        "rotateCubeRightIn"
      );
    case "rotateCubeTopIn":
      return _getPageEffect(pageIndex, "rotateCubeTopOut", "rotateCubeTopIn");
    case "rotateCubeBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateCubeBottomOut",
        "rotateCubeBottomIn"
      );

    case "rotateCarouselLeftIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselLeftOut",
        "rotateCarouselLeftIn"
      );
    case "rotateCarouselRightIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselRightOut",
        "rotateCarouselRightIn"
      );
    case "rotateCarouselTopIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselTopOut",
        "rotateCarouselTopIn"
      );
    case "rotateCarouselBottomIn":
      return _getPageEffect(
        pageIndex,
        "rotateCarouselBottomOut",
        "rotateCarouselBottomIn"
      );

    case "rotateSidesIn":
      return _getPageEffect(pageIndex, "rotateSidesOut", "rotateSidesIn");
    case "rotateSlideIn":
      return _getPageEffect(pageIndex, "rotateSlideOut", "rotateSlideIn");
  }
}

// duration
function _getPageDuration(
  pageIndex: number,
  currentDuration = 600,
  nextDuration = 600
): number {
  switch (pageIndex) {
    case currentPageIndex:
      return currentDuration;
    case nextPageIndex:
      return nextDuration;
    default:
      return props.duration;
  }
}
function getPageDuration(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): number {
  switch (routeEffect) {
    case "moveFromLeftFadePrevFold":
    case "moveFromRightFadePrevFold":
    case "moveFromTopFadePrevFold":
    case "moveFromBottomFadePrevFold":
      return _getPageDuration(pageIndex, 700, 700);
    case "moveToLeftEasing":
    case "moveToRightEasing":
    case "moveToTopEasing":
    case "moveToBottomEasing":
    case "moveFromLeftPrevScaleToSmall":
    case "moveFromRightPrevScaleToSmall":
    case "moveFromTopPrevScaleToSmall":
    case "moveFromBottomPrevScaleToSmall":
      return _getPageDuration(pageIndex, 700, 600);
    case "scaleSmall":
      return _getPageDuration(pageIndex, 700, 500);
    case "scaleLarge":
      return _getPageDuration(pageIndex, 500, 700);
    case "scaleToLargePrevScaleToSmall":
      return _getPageDuration(pageIndex, 400, 400);
    case "scaleFromSmallPrevMoveToLeft":
    case "scaleFromSmallPrevMoveToRight":
    case "scaleFromSmallPrevMoveToTop":
    case "scaleFromSmallPrevMoveToBottom":
      return _getPageDuration(pageIndex, 600, 700);
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
      return _getPageDuration(pageIndex, 800, 600);
    default:
      return props.duration;
  }
}

// js ease
let timingFunction = props.timingFunction;
// function _getPageEaseFunctionFromCSS(pageIndex: number): EasingFunction {
//   switch (pageIndex) {
//     case currentPageIndex:
//     case nextPageIndex:
//       return isForward()
//         ? CSSToJSEasing(timingFunction)
//         : reverseCSSToJSEasing(timingFunction);
//     default:
//       return props.easeFunction;
//   }
// }
function _getPageEaseFunction(pageIndex: number): EasingFunction {
  switch (pageIndex) {
    // case currentPageIndex:
    //   return props.differentEaseFunction;
    // case nextPageIndex:
    default:
      return props.easeFunction;
  }
}
function getPageEaseFunction(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): EasingFunction {
  /// css animation 和 js animation 混合
  // switch (routeEffect) {
  //   case "moveFromLeftFadePrevFold":
  //   case "moveFromRightFadePrevFold":
  //   case "moveFromTopFadePrevFold":
  //   case "moveFromBottomFadePrevFold":
  //     timingFunction = "ease";
  //     return _getPageEaseFunctionFromCSS(pageIndex);
  //   default:
  //     timingFunction = props.timingFunction;
  //     break;
  // }
  /// js animation
  switch (routeEffect) {
    case "moveToLeftEasing":
    case "moveToRightEasing":
    case "moveToTopEasing":
    case "moveToBottomEasing":
      switch (pageIndex) {
        case currentPageIndex:
          timingFunction = isForward() ? "ease-in-out" : "ease";
          break;
        case nextPageIndex:
        default:
          timingFunction = isForward() ? "ease" : "ease-in-out";
      }
      return _getPageEaseFunction(pageIndex);
    default:
      timingFunction = props.timingFunction;
      return props.easeFunction;
  }
}

// delay
function _getPageDelay(pageIndex: number, delay = 300): number {
  switch (pageIndex) {
    case nextPageIndex:
      return props.delay + delay;
    default:
      return props.delay;
  }
}
function getPageDelay(
  routeEffect: SupportedRouteEffect,
  pageIndex: number
): number {
  switch (routeEffect) {
    case "flipToLeft":
    case "flipToRight":
    case "flipToTop":
    case "flipToBottom":
    case "newspaper":
      return _getPageDelay(pageIndex, 500);
    case "scaleSmall":
    case "scaleLarge":
      return _getPageDelay(pageIndex, 300);
    case "scaleToLargePrevScaleToSmall":
      return _getPageDelay(pageIndex, 400);
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
    case "rotateSidesIn":
      return _getPageDelay(pageIndex, 200);
    case "pullFromLeftPrevPush":
    case "pullFromRightPrevPush":
    case "pullFromTopPrevPush":
    case "pullFromBottomPrevPush":
      return _getPageDelay(pageIndex, 180);
    default:
      return props.delay;
  }
}

let currentZIndex = 2;
let nextZIndex = 2;
const sameOnTop = () => {
  currentZIndex = 2;
  nextZIndex = 2;
};
const nextOnTop = () => {
  currentZIndex = 2;
  nextZIndex = 3;
};
const currentOnTop = () => {
  currentZIndex = 3;
  nextZIndex = 2;
};
function setRunningStyle(pageIndex: number) {
  switch (props.effect) {
    case "moveToLeftEasing":
    case "moveToRightEasing":
    case "moveToTopEasing":
    case "moveToBottomEasing":
      currentOnTop();
      break;
    case "scaleFromSmallPrevMoveToLeft":
    case "scaleFromSmallPrevMoveToRight":
    case "scaleFromSmallPrevMoveToTop":
    case "scaleFromSmallPrevMoveToBottom":
    case "rotateFall":
    case "rotateRoomLeftIn":
    case "rotateRoomRightIn":
    case "rotateRoomTopIn":
    case "rotateRoomBottomIn":
    case "rotateCubeLeftIn":
    case "rotateCubeRightIn":
    case "rotateCubeTopIn":
    case "rotateCubeBottomIn":
    case "rotateCarouselLeftIn":
    case "rotateCarouselRightIn":
    case "rotateCarouselTopIn":
    case "rotateCarouselBottomIn":
      isForward() ? currentOnTop() : nextOnTop();
      break;
    case "moveFromLeftPrevFade":
    case "moveFromRightPrevFade":
    case "moveFromTopPrevFade":
    case "moveFromBottomPrevFade":
    case "moveFromLeftPrevScaleToSmall":
    case "moveFromRightPrevScaleToSmall":
    case "moveFromTopPrevScaleToSmall":
    case "moveFromBottomPrevScaleToSmall":
    case "glueToLeft":
    case "glueToRight":
    case "glueToTop":
    case "glueToBottom":
      isForward() ? nextOnTop() : currentOnTop();
      break;
    default:
      sameOnTop();
      break;
  }
  if (isValidPageIndex(currentPageIndex) && currentPageIndex == pageIndex) {
    pageElements[currentPageIndex].style.zIndex = `${currentZIndex}`;
    pageElements[currentPageIndex].style.visibility = "visible";
  }
  if (isValidPageIndex(nextPageIndex) && nextPageIndex == pageIndex) {
    pageElements[nextPageIndex].style.zIndex = `${nextZIndex}`;
    pageElements[nextPageIndex].style.visibility = "visible";
  }
}

// css animation 与 js animation 有时间差
function emitsEnd(pageIndex: number) {
  nextOnTop();
  if (pageIndex == currentPageIndex) {
    pageElements[currentPageIndex].style.zIndex = `${currentZIndex}`;
    pageElements[currentPageIndex].style.visibility = "hidden";
  }
  if (pageIndex == nextPageIndex) {
    pageElements[nextPageIndex].style.zIndex = `${nextZIndex}`;
    pageElements[nextPageIndex].style.visibility = "visible";
    onRouteCompletes.shift()?.();
    emits("routeChangeEnd", currentPageIndex, nextPageIndex, pages.length);
    currentPageIndex = nextPageIndex;
  }
}

const routesRef = ref();
function renderPages(appendCount = 0, isReset = false) {
  if (!routesRef.value) {
    return;
  }
  let renderCount = 0;
  render(
    h(
      "div",
      {
        class: "routes-pages",
        style: {
          display: "block",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          position: "relative"
        }
      },
      pages.map((page, index) => {
        // 初始化
        const pageEffect = getPageEffect(props.effect, index);
        return h(
          "div",
          Object.assign(
            {
              class: "routes-page",
              style: {
                display: "block",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                position: "absolute",
                top: "0",
                left: "0",
                backfaceVisibility: "hidden",
                transform: "translate3d(0, 0, 0)",
                transformStyle: "preserve-3d",
                visibility: currentPageIndex == index ? "visible" : "hidden",
                zIndex: isReset ? "1" : "0"
              }
            },
            page.pageKey
              ? { key: page.pageKey }
              : {
                  // 无 key 不会重复利用
                }
          ),
          h(
            Page,
            {
              // ...page.pageAttributes,
              effect: pageEffect,
              duration: getPageDuration(props.effect, index),
              easeFunction: getPageEaseFunction(props.effect, index),
              timingFunction: timingFunction,
              delay: getPageDelay(props.effect, index),
              onMount: (open, close) => {
                // console.log("onMount ", index);
                pageActions[index] = {
                  open,
                  close
                };
                // 渲染结束
                if (++renderCount == appendCount) {
                  onRenderComplete?.();
                  emits("renderComplete");
                }
              },
              onPageOpenStart: () => {
                // console.log("onPageOpenStart ", index);
                setRunningStyle(index);
              },
              onPageOpenEnd: () => {
                // console.log("onPageOpenEnd ", index);
                emitsEnd(index);
              },
              onPageCloseStart: () => {
                // console.log("onPageCloseStart ", index);
                setRunningStyle(index);
              },
              onPageCloseEnd: () => {
                // console.log("onPageCloseEnd ", index);
                emitsEnd(index);
              }
            },
            // () => h(page.component, page.attributes)
            {
              default: () =>
                page.component instanceof HTMLElement
                  ? h(
                      "div",
                      {
                        ...page.attributes,
                        innerHTML: page.component.innerHTML // ce 动态设置的 props 丢失 ！
                      }
                      // elementsToVNodes(page.component)
                    )
                  : isVNode(page.component)
                  ? page.component
                  : h(page.component, page.attributes, () => page.child)
            }
          )
        );
      })
    ),
    routesRef.value
  );
  pageElements = routesRef.value.children[0].children;
}

function reset(resetPages: PageArgument[] = []) {
  pages = [...resetPages];
  pages.forEach((p) => {
    p.pageKey = p.pageKey || generateUUID();
  });
  onRenderComplete = () => {
    currentPageIndex = -1;
    goto(0);
  };
  renderPages(pages.length, true);
}

function push(page: PageArgument) {
  page.pageKey = page.pageKey || generateUUID();
  pages.push(page);
  onRenderComplete = () => {
    goto(pages.length - 1);
  };
  renderPages(1, pages.length == 1);
}

function pop() {
  if (pages.length <= 1) {
    return;
  }
  goto(pages.length - 2, () => {
    pages.splice(pages.length - 1, 1);
    // pop 中 onRenderComplete 不会执行
    currentPageIndex = pages.length - 1;
    renderPages();
  });
}

function forward() {
  goto(currentPageIndex + 1);
}

function back() {
  goto(currentPageIndex - 1);
}

// const schedule = new ScheduleDependOnResult({ concurrency: 1 });

function goto(index: number, cb?: () => void) {
  // schedule.asap(
  //   createTask({
  //     fn: () =>
  new Promise<void>((resolve) => {
    const onRouteComplete = () => {
      cb?.();
      resolve();
    };
    if (!isValidPageIndex(index)) {
      onRouteComplete();
      return;
    }
    nextPageIndex = index;
    if (nextPageIndex == currentPageIndex) {
      onRouteComplete();
      return;
    }
    // 执行动画
    onRouteCompletes.push(onRouteComplete);
    // 修改 page prop
    renderPages();
    emits("routeChangeStart", currentPageIndex, nextPageIndex, pages.length);
    if (isForward()) {
      // forward
      if (isValidPageIndex(currentPageIndex)) {
        pageActions[currentPageIndex].open();
      }
      if (isValidPageIndex(nextPageIndex)) {
        pageActions[nextPageIndex].open();
      }
    } else {
      // back
      if (isValidPageIndex(currentPageIndex)) {
        pageActions[currentPageIndex].close();
      }
      if (isValidPageIndex(nextPageIndex)) {
        pageActions[nextPageIndex].close();
      }
    }
  });
  //   })
  // );
}

defineExpose({
  reset,
  push,
  pop,
  forward,
  back,
  goto,
  currentPageIndex: () => currentPageIndex,
  totalPages: () => pages.length
});
</script>

<template>
  <div
    ref="routesRef"
    class="routes"
    style="
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    "
  ></div>
</template>
